import React, { useState } from 'react';
import PropTypes from 'prop-types';

import InputGroup from '~components/forms/inputs/Field';
import * as Yup from 'yup';
import { phone } from '~utils/regex';
import { useSelector } from 'react-redux';
import emailSpellChecker from '@zootools/email-spell-checker';

export const phoneRequiredValidator = Yup.string()
  .required('Cell phone number is required')
  .matches(phone, 'Must be a valid 10-digit phone number');

export const emailRequiredValidator = Yup.string()
  .email('Must be a valid email address')
  .required('Email address is required');

export const ContactInformationBlockSchema = {
  email: emailRequiredValidator,
  phone: Yup.string()
    .nullable()
    .matches(phone, 'Must be a valid 10-digit phone number'),
};

export const ContactInformationBlockSchemaPhoneRequired = {
  ...ContactInformationBlockSchema,
  phone: phoneRequiredValidator,
};

export const ContactInformationBlockCollegeEditionSchema = {
  email: Yup.string()
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.+-]+\.edu$/,
      'Email address must end in .edu'
    )
    .required('Email address is required'),
  phone: Yup.string()
    .nullable()
    .matches(phone, 'Must be a valid 10-digit phone number'),
};

function ContactInformationBlock({
  errors,
  isEmailRequired,
  isPhoneRequired,
  showAsOptional,
  emailHelpText,
  setFieldValue,
  showEmail,
  showPhone,
  isDisabled,
}) {
  const sourcing = useSelector((state) => state.sourcing);
  const [emailSuggestion, setEmailSuggestion] = useState(null);

  const handleEmailBlur = (e) => {
    const emailValue = e.target.value;
    const suggestion = emailSpellChecker.run({ email: emailValue });
    if (suggestion && suggestion.full !== emailValue) {
      setEmailSuggestion(suggestion.full);
    } else {
      setEmailSuggestion(null);
    }
  };

  const applySuggestion = () => {
    setFieldValue('email', emailSuggestion);
    setEmailSuggestion(null);
  };

  return (
    <div className='space-y-5'>
      {showEmail && (
        <>
          <InputGroup
            name='email'
            type='email'
            label={sourcing.edition === 'college' ? 'School email' : 'Email'}
            errors={errors}
            required={isEmailRequired}
            showAsOptional={showAsOptional}
            helpText={emailHelpText}
            onBlur={handleEmailBlur}
            disabled={isDisabled}
          />
          {emailSuggestion && (
            <span className='label-text-alt mx-1 mt-1 text-sm font-bold text-primary'>
              Did you mean <strong>{emailSuggestion}</strong>?{' '}
              <button
                type='button'
                onClick={applySuggestion}
                className='font-bold text-blue-600 underline hover:text-blue-800'
              >
                Yes, fix it
              </button>
            </span>
          )}
        </>
      )}
      {showPhone && (
        <InputGroup
          name='phone'
          label={isPhoneRequired ? 'Cell phone' : 'Cell phone (recommended)'}
          type='tel'
          errors={errors}
          required={isPhoneRequired}
          showAsOptional={showAsOptional}
          inputMask='(000) 000-0000'
          autoComplete='tel-national'
          helpText={
            isPhoneRequired
              ? ''
              : 'If provided, we will send you election reminders via text.'
          }
          disabled={isDisabled}
        />
      )}
    </div>
  );
}

ContactInformationBlock.propTypes = {
  errors: PropTypes.object.isRequired,
  isEmailRequired: PropTypes.bool,
  isPhoneRequired: PropTypes.bool,
  showAsOptional: PropTypes.bool,
  emailHelpText: PropTypes.string,
  setFieldValue: PropTypes.func.isRequired,
  showEmail: PropTypes.bool,
  showPhone: PropTypes.bool,
  isDisabled: PropTypes.bool,
};

ContactInformationBlock.defaultProps = {
  isEmailRequired: true,
  isPhoneRequired: false,
  showAsOptional: false,
  emailHelpText: '',
  showEmail: true,
  showPhone: true,
  isDisabled: false,
};

export default ContactInformationBlock;
